import React, { useEffect, useRef, useState, useMemo } from "react";
import { Card, Container, Row, Col } from "react-bootstrap";
import buzzwordsData from "../../data/buzzwords.json";
import { BuzzwordSchema } from "../../data/validators";
import styles from "./BuzzwordCards.module.css";

interface Buzzword {
  id: number;
  title: string;
  description: string;
}

/**
 * Section that displays buzz‑word cards.
 * Cards fade/slide in when they enter the viewport.
 */
const BuzzwordCards: React.FC = () => {
  const [visibleIds, setVisibleIds] = useState<Set<number>>(new Set());
  const refs = useRef<Record<number, HTMLDivElement | null>>({});

  // Validate data at runtime (fails fast if JSON shape is wrong)
  const buzzwords = useMemo<Buzzword[]>(() => BuzzwordSchema.array().parse(buzzwordsData), []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const id = Number(entry.target.getAttribute("data-id"));
          if (entry.isIntersecting) {
            setVisibleIds((prev) => new Set(prev).add(id));
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.2 },
    );

    buzzwords.forEach((b) => {
      const el = refs.current[b.id];
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, [buzzwords]);

  return (
    <section className={styles.buzzwordSection}>
      <Container>
        <h2 className={styles.sectionTitle}>Our Core Strengths</h2>
        <Row className="g-4">
          {buzzwords.map((b) => (
            <Col key={b.id} xs={12} md={4}>
              <div
                className={`${styles.cardWrapper} ${visibleIds.has(b.id) ? styles.visible : ""
                  }`}
                data-id={b.id}
                ref={(el) => (refs.current[b.id] = el)}
              >
                <Card className={styles.card}>
                  <Card.Body>
                    <Card.Title>{b.title}</Card.Title>
                    <Card.Text>{b.description}</Card.Text>
                  </Card.Body>
                </Card>
              </div>
            </Col>
          ))}
        </Row>
      </Container>
    </section>
  );
};

export default BuzzwordCards;
